import { useEffect, useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { AlertTriangle, CreditCard, KeyRound, PauseCircle, PlayCircle } from 'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  useEnterprisePartner,
  useEnterprisePartnerIdentities,
} from '@/hooks/useEnterprisePartners';
import {
  useRotatePartnerSecret,
  useUpdatePartnerStatus,
} from '@/hooks/useEnterpriseSsoActions';
import { useSeatUsage } from '@/hooks/useEnterpriseSeatUsage';
import { useSetPartnerBilling } from '@/hooks/useProvisionEnterpriseMember';
import { PartnerAdminList } from './PartnerAdminList';
import { PartnerIdentityList } from './PartnerIdentityList';
import { PartnerAuditLog } from './PartnerAuditLog';
import { PartnerSecretRotation } from './PartnerSecretRotation';
import { PartnerIntegrationDocs } from './PartnerIntegrationDocs';
import { captureError } from '@/lib/sentry';

interface PartnerDetailProps {
  partnerId: string;
  /** Reserved for future use (e.g., pop back to list after a permanent action). Currently unused. */
  onBack?: () => void;
}

/**
 * Phase 3 Task 19. Tabbed detail view for a single enterprise partner.
 * Tabs: Overview / Admins / Identities / Audit Log / Danger Zone.
 *
 * Composes the components from Tasks 16/17/18, plus inline Overview/Danger
 * Zone panels. Renders the secret-rotation modal in response to user
 * action (rotate-secret button in Danger Zone).
 */
export function PartnerDetail({ partnerId }: PartnerDetailProps): JSX.Element {
  const { toast } = useToast();
  const partnerQuery = useEnterprisePartner(partnerId);
  const identitiesQuery = useEnterprisePartnerIdentities(partnerId);
  const rotateSecret = useRotatePartnerSecret();
  const updateStatus = useUpdatePartnerStatus();

  const [rotatedSecret, setRotatedSecret] = useState<{
    plaintext: string;
    rotatedAt: string;
  } | null>(null);
  const [confirmSuspend, setConfirmSuspend] = useState(false);

  const partner = partnerQuery.data;

  if (partnerQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading partner…</div>;
  }
  if (partnerQuery.error || !partner) {
    return (
      <div className="text-sm text-destructive">
        Failed to load partner. {partnerQuery.error?.message ?? 'Unknown error'}
      </div>
    );
  }

  const handleRotateSecret = async () => {
    try {
      const result = await rotateSecret.mutateAsync(partnerId);
      setRotatedSecret({
        plaintext: result.signing_secret_plaintext,
        rotatedAt: new Date().toISOString(),
      });
    } catch (err) {
      captureError(err as Error, { feature: 'enterprise-partner-detail-handle-rotate-secret' });
      toast({
        variant: 'destructive',
        title: 'Rotation failed',
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const handleConfirmSuspend = async () => {
    setConfirmSuspend(false);
    try {
      await updateStatus.mutateAsync({ partner_id: partnerId, status: 'suspended' });
      toast({ title: 'Partner suspended', description: `${partner.name} is now suspended. Active sessions are being signed out.` });
    } catch (err) {
      captureError(err as Error, { feature: 'enterprise-partner-detail-handle-confirm-suspend' });
      toast({
        variant: 'destructive',
        title: 'Suspend failed',
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const handleReactivate = async () => {
    try {
      await updateStatus.mutateAsync({ partner_id: partnerId, status: 'active' });
      toast({ title: 'Partner reactivated', description: `${partner.name} is now active.` });
    } catch (err) {
      captureError(err as Error, { feature: 'enterprise-partner-detail-handle-reactivate' });
      toast({
        variant: 'destructive',
        title: 'Reactivation failed',
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const identityCount = identitiesQuery.data?.length ?? 0;
  const isSuspended = partner.status === 'suspended';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold">{partner.name}</h2>
          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
            <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{partner.slug}</code>
            <Badge variant={partner.status === 'active' ? 'default' : partner.status === 'suspended' ? 'secondary' : 'destructive'}>
              {partner.status}
            </Badge>
            <span>·</span>
            <span>{partner.provisioning_mode === 'shared' ? 'Shared workspace' : 'Per-user workspaces'}</span>
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="integration">Integration</TabsTrigger>
          <TabsTrigger value="admins">Admins</TabsTrigger>
          <TabsTrigger value="identities">
            Identities {identityCount > 0 && <span className="ml-1 text-xs text-muted-foreground">({identityCount})</span>}
          </TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
          <TabsTrigger value="danger" className="text-destructive data-[state=active]:text-destructive">
            Danger Zone
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 pt-4">
          <OverviewField label="Provisioning mode" value={partner.provisioning_mode === 'shared' ? 'Shared workspace' : 'Per-user workspaces'} />
          {partner.provisioning_mode === 'shared' && (
            <OverviewField label="Default member role" value={partner.default_member_role ?? '—'} />
          )}
          <OverviewField label="Portal URL" value={partner.portal_url ?? '—'} mono />
          <OverviewField
            label="Secret rotated"
            value={
              partner.signing_secret_rotated_at
                ? `${formatDistanceToNow(new Date(partner.signing_secret_rotated_at), { addSuffix: true })} (${format(new Date(partner.signing_secret_rotated_at), 'PPpp')})`
                : 'Never (still using initial secret)'
            }
          />
          <OverviewField label="Identity count" value={identityCount.toString()} />
          <OverviewField label="Created" value={`${formatDistanceToNow(new Date(partner.created_at), { addSuffix: true })} (${format(new Date(partner.created_at), 'PPpp')})`} />

          <PartnerBillingSection partnerId={partnerId} />
        </TabsContent>

        <TabsContent value="integration" className="pt-4">
          <PartnerIntegrationDocs partner={partner} />
        </TabsContent>

        <TabsContent value="admins" className="pt-4">
          <PartnerAdminList partnerId={partnerId} />
        </TabsContent>

        <TabsContent value="identities" className="pt-4">
          <PartnerIdentityList partnerId={partnerId} partnerMode={partner.provisioning_mode} />
        </TabsContent>

        <TabsContent value="audit" className="pt-4">
          <PartnerAuditLog partnerId={partnerId} />
        </TabsContent>

        <TabsContent value="danger" className="pt-4 space-y-4">
          <div className="rounded-lg border border-destructive/30 p-4 space-y-3">
            <div className="flex items-start gap-2">
              <KeyRound className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold">Rotate signing secret</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Generate a fresh secret and replace the active one. The previous secret continues to work for 24 hours so the partner can update their portal without downtime.
                </p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleRotateSecret}
                disabled={rotateSecret.isPending}
              >
                {rotateSecret.isPending ? 'Rotating…' : 'Rotate'}
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-destructive/30 p-4 space-y-3">
            <div className="flex items-start gap-2">
              {isSuspended ? (
                <PlayCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              ) : (
                <PauseCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <h4 className="font-semibold">{isSuspended ? 'Reactivate partner' : 'Suspend partner'}</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  {isSuspended
                    ? "The partner is currently suspended. Reactivating allows the partner's users to sign in via SSO again."
                    : 'Suspending blocks all SSO sign-ins for this partner and immediately signs out every active session.'}
                </p>
              </div>
              <Button
                variant={isSuspended ? 'default' : 'destructive'}
                size="sm"
                onClick={isSuspended ? handleReactivate : () => setConfirmSuspend(true)}
                disabled={updateStatus.isPending}
              >
                {isSuspended ? 'Reactivate' : 'Suspend'}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {rotatedSecret !== null && (
        <PartnerSecretRotation
          signingSecretPlaintext={rotatedSecret.plaintext}
          rotatedAt={rotatedSecret.rotatedAt}
          onAcknowledge={() => setRotatedSecret(null)}
        />
      )}

      <AlertDialog open={confirmSuspend} onOpenChange={setConfirmSuspend}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Suspend {partner.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Suspending blocks all SSO sign-ins and immediately signs out every active session for this partner's provisioned users. They will see a "your enterprise integration is paused" message until you reactivate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSuspend} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Suspend
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function OverviewField({ label, value, mono = false }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3 text-sm">
      <div className="text-muted-foreground">{label}</div>
      <div className={mono ? 'font-mono text-xs break-all' : ''}>{value}</div>
    </div>
  );
}

/** Select value for "unconfigured" — Radix Select can't use an empty string. */
const UNCONFIGURED = 'none';
type BillingModelChoice = 'none' | 'licensed' | 'per_seat';

function billingModelLabel(model: 'licensed' | 'per_seat' | null): string {
  if (model === 'licensed') return 'Licensed';
  if (model === 'per_seat') return 'Per-seat';
  return 'None (unconfigured)';
}

/**
 * Super-admin "Plan & seats" control (#609, Task 8b). Lives on PartnerDetail's
 * Overview tab — the existing super-admin-only partner-management interface.
 * Shows the current billing model + licensed seats (and current seats_used for
 * context) and lets staff set them via enterprise_set_partner_billing.
 *
 * The setter RPC is gated server-side on caller_can_manage_partner (super_admin
 * only) — the real boundary. This panel is also cosmetically super-admin-only
 * (the whole AdminBar Enterprise panel is). An enterprise *partner* admin can
 * never reach this control nor the RPC, so they cannot raise their own cap.
 *
 * Current model/seats are sourced from partner_seat_usage (which returns
 * billing_model + licensed_seats + seats_used) so no extra partner-read column
 * plumbing is needed.
 */
function PartnerBillingSection({ partnerId }: { partnerId: string }): JSX.Element {
  const { toast } = useToast();
  const seatUsage = useSeatUsage(partnerId);
  const setBilling = useSetPartnerBilling();

  const [open, setOpen] = useState(false);
  const [model, setModel] = useState<BillingModelChoice>(UNCONFIGURED);
  const [seats, setSeats] = useState('');

  const usage = seatUsage.data;

  // When the dialog opens, seed the form from the live values.
  useEffect(() => {
    if (open && usage) {
      setModel((usage.billing_model ?? UNCONFIGURED) as BillingModelChoice);
      setSeats(usage.licensed_seats != null ? String(usage.licensed_seats) : '');
    }
  }, [open, usage]);

  const handleSave = async () => {
    const billingModel = model === UNCONFIGURED ? null : model;
    let licensedSeats: number | null = null;
    if (billingModel === 'licensed') {
      const parsed = Number.parseInt(seats, 10);
      if (!Number.isFinite(parsed) || parsed < 0) {
        toast({
          variant: 'destructive',
          title: 'Invalid seat count',
          description: 'Enter a non-negative whole number of seats.',
        });
        return;
      }
      licensedSeats = parsed;
    }
    try {
      await setBilling.mutateAsync({
        partner_id: partnerId,
        billing_model: billingModel,
        licensed_seats: licensedSeats,
      });
      toast({ title: 'Plan updated', description: 'Billing model and seats saved.' });
      setOpen(false);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not update plan',
        description: err instanceof Error ? err.message : 'Please try again.',
      });
    }
  };

  const currentModel = usage?.billing_model ?? null;
  const currentSeats = usage?.licensed_seats ?? null;
  const seatsUsed = usage?.seats_used ?? null;

  return (
    <div className="mt-6 rounded-lg border p-4 space-y-3">
      <div className="flex items-start gap-2">
        <CreditCard className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-semibold">Plan &amp; seats</h4>
          <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
            <div>
              Billing model:{' '}
              <span className="text-foreground">{billingModelLabel(currentModel)}</span>
            </div>
            {currentModel === 'licensed' && (
              <div>
                Licensed seats:{' '}
                <span className="text-foreground">{currentSeats ?? '—'}</span>
                {seatsUsed != null && currentSeats != null && (
                  <span> ({seatsUsed} of {currentSeats} used)</span>
                )}
              </div>
            )}
            {currentModel !== 'licensed' && seatsUsed != null && (
              <div>
                Seats used: <span className="text-foreground">{seatsUsed}</span>
                {currentModel === null && ' (uncapped — unconfigured)'}
                {currentModel === 'per_seat' && ' (uncapped — per-seat)'}
              </div>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          disabled={seatUsage.isLoading}
        >
          Edit
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit plan &amp; seats</DialogTitle>
            <DialogDescription>
              Set this enterprise&rsquo;s billing model. A licensed plan hard-caps
              the number of seats; per-seat and unconfigured plans are uncapped.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Billing model</label>
              <Select
                value={model}
                onValueChange={(v) => setModel(v as BillingModelChoice)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNCONFIGURED}>None (unconfigured)</SelectItem>
                  <SelectItem value="licensed">Licensed</SelectItem>
                  <SelectItem value="per_seat">Per-seat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {model === 'licensed' && (
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="licensed-seats">
                  Licensed seats
                </label>
                <Input
                  id="licensed-seats"
                  type="number"
                  min={0}
                  value={seats}
                  onChange={(e) => setSeats(e.target.value)}
                  placeholder="e.g. 50"
                />
                {seatsUsed != null && (
                  <p className="text-xs text-muted-foreground">
                    {seatsUsed} {seatsUsed === 1 ? 'seat is' : 'seats are'} currently
                    in use — set the cap at or above this.
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={handleSave} disabled={setBilling.isPending}>
              {setBilling.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
